import { ReactElement, useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { connect, ConnectedProps } from 'react-redux';
import clsx from 'clsx';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { assignUid } from 'data-layer-cr/dist/functions/helpers';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import {
  useFormHook,
  colors,
  H4Text,
  NetworkIndicator,
  helpers,
} from 'design-system';

import {
  getInspectionByLicense,
  transferInspection,
  updateInspections,
} from 'src/store/inspection/actions';
import { SEARCH_INSPECTION } from 'src/store/inspection/requestIdentifier';

import {
  resetFiles,
  updateInspectionReportValue,
  updateVehicleRegisterValue,
} from 'src/store/quotation/actions';

// models
import Inspection from 'src/models/helperTypes/Inspection';
import { RootState } from 'src/store/rootReducer';

// resources
import searchGirl from 'src/resources/search_girl.svg';
import searchGirlWarning from 'src/resources/search_girl_warning.svg';

// validation schema
import makeInputValidationSchema from './makeInputValidationSchema';

// subcomponents
import { InspectionSearchBar, InspectionCard } from '../../components';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    rowMargin: {
      margin: theme.spacing(2),
      width: '100%',
      padding: theme.spacing(0, 3),
    },
    message: {
      fontSize: theme.spacing(1.75),
      fontWeight: 'bold',
      width: theme.spacing(30.625),
      textAlign: 'center',
    },
    info: {
      color: colors.abbey,
    },
    error: {
      color: colors.bright_pink,
    },
  }),
);

type InspectionSearchData = {
  input: string;
};

export function InspectionSearch({
  inspections,
  searchInspectionAction,
  updateInspectionsAction,
  inspectionReportBase64,
  vehicleRegistryBase64,
  transferInspectionAction,
  requestInspectionReport,
  requestVehicleRegistry,
  resetFilesAction,
}: PropsFromRedux): ReactElement {
  const classes = useStyles();
  const [searched, setSearched] = useState(false);
  const searchStatus = useNetworkObserver(SEARCH_INSPECTION);
  const cancelTokenSource = useRef(axios.CancelToken.source()).current;

  // cleanup list on unmount
  useEffect(
    () => {
      return (): void => {
        updateInspectionsAction([]);
        resetFilesAction();
        cancelTokenSource.cancel();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const submitData = (data: InspectionSearchData): void => {
    setSearched(true);
    searchInspectionAction(data.input);
  };

  const { getInputProps, handleSubmit, errors } = useFormHook(
    { input: '' },
    submitData,
    makeInputValidationSchema,
    {
      deferErrorCheck: true,
    },
  );

  const searchValue = getInputProps('input').value as string;
  const searchOnChange = getInputProps('input').onChange;
  const errorMessage = getInputProps('input').errorText;
  const hintMessage = 'Recuerda ingresar el número completo de la placa';
  const notFoundMessage = `No se encontró una inspección para la placa ${searchValue}`;

  const mappedInspections = assignUid(inspections);

  const hasErrors = Object.keys(errors).length > 0;
  const showArt = inspections.length < 1 && searchStatus !== 'requesting';
  const shouldDisplayErrorArt = hasErrors || (searched && showArt);
  const shouldDisplayHintMessage =
    showArt && !hasErrors && !searched && searchStatus !== 'requesting';
  const shouldDisplayEmptyMessage =
    showArt && !hasErrors && searched && searchStatus !== 'requesting';

  const isReportDefined = useMemo(
    () => ![inspectionReportBase64, vehicleRegistryBase64].includes(''),
    [inspectionReportBase64, vehicleRegistryBase64],
  );

  const inspectionReportName = 'inspectionReport';
  const vehicleRegistryName = 'vehicleRegistry';
  const inspectionReportKey = 'inspectionReport';
  const vehicleRegistryKey = 'vehicle-registry-image';

  function downloadFile(fileBase64: string, fileName: string): void {
    let extension = '.pdf';
    const link = document.createElement('a');
    const mimeType = helpers.extractMime(fileBase64);

    if (mimeType === 'image/png') {
      extension = '.png';
    }

    if (mimeType === 'image/jpeg') {
      extension = '.jpeg';
    }

    link.href = fileBase64;
    link.setAttribute('download', fileName + extension);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
  }

  // make sure to only send request download once. Subsequent ones should simply refer the redux store
  function handleDownloadPdf(urlSalt: string): void {
    requestInspectionReport(urlSalt, inspectionReportKey, cancelTokenSource);
    requestVehicleRegistry(urlSalt, vehicleRegistryKey, cancelTokenSource);
  }

  // observes when file is successfully downloaded and delivers it to the client
  useEffect(() => {
    if (isReportDefined) {
      downloadFile(inspectionReportBase64, inspectionReportName);
      downloadFile(vehicleRegistryBase64, vehicleRegistryName);
      resetFilesAction();
    }
  }, [
    isReportDefined,
    inspectionReportBase64,
    vehicleRegistryBase64,
    resetFilesAction,
  ]);

  return (
    <div className={classes.root}>
      <InspectionSearchBar
        inputName="input"
        defaultValue={searchValue}
        placeholder="Ingresa la placa..."
        onChange={(newValue: string): void => {
          setSearched(false);
          searchOnChange(newValue);
        }}
        onSubmit={(): void => {
          // clear list before every search
          updateInspectionsAction([]);
          handleSubmit();
        }}
        data-testid="search-bar"
      />
      <NetworkIndicator
        message="Buscando..."
        requesting={searchStatus === 'requesting'}
        data-testid="spinner"
      />
      {showArt && (
        <img
          className={classes.rowMargin}
          src={shouldDisplayErrorArt ? searchGirlWarning : searchGirl}
          alt="search_art"
          width={244}
          height={221}
          data-testid="search-art"
        />
      )}
      {shouldDisplayHintMessage && (
        <H4Text
          className={clsx(classes.message, classes.info)}
          data-testid="hint-msg"
        >
          {hintMessage}
        </H4Text>
      )}
      {shouldDisplayEmptyMessage && (
        <H4Text
          className={clsx(classes.error, classes.info)}
          data-testid="not-found-msg"
        >
          {notFoundMessage}
        </H4Text>
      )}
      {showArt && hasErrors && (
        <H4Text
          className={clsx(classes.message, classes.error)}
          data-testid="error-msg"
        >
          {errorMessage}
        </H4Text>
      )}
      {!showArt &&
        mappedInspections.map((ele, index) => (
          <div className={classes.rowMargin} key={ele.id}>
            <InspectionCard
              inspection={ele.data}
              data-testid={`inspection-card-${index}`}
              onDownload={handleDownloadPdf}
              onTransfer={transferInspectionAction}
            />
          </div>
        ))}
    </div>
  );
}

export const mapStateToProps = (
  state: RootState,
): {
  inspections: Inspection[];
  inspectionReportBase64: string;
  vehicleRegistryBase64: string;
} => ({
  inspections: state.inspection.inspections,
  inspectionReportBase64: state.quotation.inspectionReportBase64,
  vehicleRegistryBase64: state.quotation.vehicleRegisterBase64,
});

const reduxActions = {
  searchInspectionAction: getInspectionByLicense,
  updateInspectionsAction: updateInspections,
  requestInspectionReport: updateInspectionReportValue,
  requestVehicleRegistry: updateVehicleRegisterValue,
  resetFilesAction: resetFiles,
  transferInspectionAction: transferInspection,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(InspectionSearch);
